import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { elegantMenuWithHintsProps } from '../../utils/menuStyles';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, IconButton, Menu, MenuItem, Chip, Alert,
  FormControl, InputLabel, Select, Skeleton, alpha, useTheme,
  ListItemIcon, ListItemText, Tabs, Tab, Divider,
  Switch, FormControlLabel,
} from '@mui/material';
import {
  Search, PointOfSale, MoreVert, Undo, CheckCircle,
  WarningAmber, AccountBalance, PersonSearch, ReceiptLong,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feePaymentService from '../../services/feePaymentService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ReasonDialog from '../../components/common/ReasonDialog';
import { useReceipts } from '../../components/finance/useReceipts';
import { PAYMENT_METHOD_LABEL } from '../../utils/paymentMethods';
import { useTimezone } from '../../hooks/useTimezone';
import SearchableSelect from '../../components/common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import RecordFeePaymentDialog from '../../components/fees/RecordFeePaymentDialog';
import { getHashedColor } from '../../utils/colorUtils';

import {
  StudentWithDues, FeePaymentRow,
} from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import PayerCell from '../../components/fees/PayerCell';
import { payerNumber } from '../../utils/personRef';

const DEFAULT_ROWS_PER_PAGE = 25;

const FeeCollectionPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fees');

  const urlTab = (searchParams.get('tab') as 'collect' | 'recent') || 'collect';
  const [tab, setTab] = useState<'collect' | 'recent'>(urlTab);

  // Common pickers
  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  // Collect tab
  const [sessionId, setSessionId] = useState(searchParams.get('sessionId') ?? '');
  const [classId, setClassId] = useState(searchParams.get('classId') ?? '');
  const [studentSearch, setStudentSearch] = useState('');
  const debouncedSearch = useDebounce(studentSearch, 300);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [duesRows, setDuesRows] = useState<StudentWithDues[]>([]);
  const [duesTotal, setDuesTotal] = useState(0);
  const [duesPage, setDuesPage] = useState(0);
  const [duesRowsPerPage, setDuesRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [loadingDues, setLoadingDues] = useState(false);
  // Skeleton only on the initial mount for each tab. Subsequent loads keep
  // the cards / rows visible so search feels instant.
  const [duesLoadedOnce, setDuesLoadedOnce] = useState(false);

  // Recent tab
  const [recentRows, setRecentRows] = useState<FeePaymentRow[]>([]);
  const [recentTotal, setRecentTotal] = useState(0);
  const [recentPage, setRecentPage] = useState(0);
  const [recentRowsPerPage, setRecentRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [recentSearch, setRecentSearch] = useState('');
  const debouncedRecentSearch = useDebounce(recentSearch, 300);
  const [recentKpis, setRecentKpis] = useState({ collected: 0, count: 0 });
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [recentLoadedOnce, setRecentLoadedOnce] = useState(false);
  const [includeReversed, setIncludeReversed] = useState(false);

  // Dialogs — pay-target carries whichever payer id the row has. Students
  // have a studentId (and typically a personId too); admission applicants
  // have only a personId.
  const [payTarget, setPayTarget] = useState<{ personId: string | null; studentId: string | null } | null>(null);
  const [reverseTarget, setReverseTarget] = useState<FeePaymentRow | null>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<FeePaymentRow | null>(null);

  const currencyFmt = useCurrencyFormat();
  const { formatDate } = useTimezone();
  const { downloadFeeReceipt: downloadReceipt, preparingReceipt } = useReceipts();

  // ─── URL sync ─────────────────────────────────────────────────────────────

  useEffect(() => {
    const next = new URLSearchParams();
    if (tab !== 'collect') next.set('tab', tab);
    if (sessionId) next.set('sessionId', sessionId);
    if (classId) next.set('classId', classId);
    setSearchParams(next, { replace: true });
  }, [tab, sessionId, classId, setSearchParams]);

  // ─── Load pickers ────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load students with dues ─────────────────────────────────────────────

  const loadDues = useCallback(async () => {
    if (tab !== 'collect') return;
    setLoadingDues(true);
    try {
      const res = await feePaymentService.getStudentsWithDues({
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        search: debouncedSearch || undefined,
        overdueOnly,
        page: duesPage + 1,
        limit: duesRowsPerPage,
      });
      setDuesRows(res.students);
      setDuesTotal(res.pagination.totalItems);
      setDuesLoadedOnce(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load students with dues', { variant: 'error' });
    } finally {
      setLoadingDues(false);
    }
  }, [tab, sessionId, classId, debouncedSearch, overdueOnly, duesPage, duesRowsPerPage, enqueueSnackbar]);

  useEffect(() => { loadDues(); }, [loadDues]);

  // ─── Load recent payments ───────────────────────────────────────────────

  const loadRecent = useCallback(async () => {
    if (tab !== 'recent') return;
    setLoadingRecent(true);
    try {
      const res = await feePaymentService.listFeePayments({
        includeReversed,
        search: debouncedRecentSearch || undefined,
        page: recentPage + 1,
        limit: recentRowsPerPage,
      });
      setRecentRows(res.payments);
      setRecentTotal(res.pagination.totalItems);
      setRecentKpis(res.kpis);
      setRecentLoadedOnce(true);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load recent payments', { variant: 'error' });
    } finally {
      setLoadingRecent(false);
    }
  }, [tab, includeReversed, debouncedRecentSearch, recentPage, recentRowsPerPage, enqueueSnackbar]);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: FeePaymentRow) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const openReverse = (row: FeePaymentRow) => {
    setReverseTarget(row);
    closeMenu();
  };

  const confirmReverse = async (reason: string) => {
    if (!reverseTarget) return;
    await feePaymentService.reverseFeePayment(reverseTarget._id, reason);
    enqueueSnackbar(`Receipt ${reverseTarget.receiptNumber} reversed`, { variant: 'success' });
    loadRecent();
  };

  const skeletonDues = useMemo(() =>
    Array.from({ length: 6 }).map((_, i) => (
      <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
      </Paper>
    )),
    []);

  if (!canReadFees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">You don't have permission to view collection.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Collection</Typography>
          <Typography variant="body2" color="text.secondary">
            Search a student, see their outstanding, and record a payment. Every collection posts to the ledger with a receipt number.
          </Typography>
        </Box>
      </Stack>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 1, '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}
        >
          <Tab value="collect" label="Collect" icon={<PersonSearch fontSize="small" />} iconPosition="start" />
          <Tab value="recent"  label="Recent payments" icon={<AccountBalance fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tab === 'collect' && (
        <>
          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <SearchableSelect
                label="Session"
                value={sessionId}
                onChange={v => { setSessionId(v); setDuesPage(0); }}
                options={buildSessionOptions(sessions, theme)}
                placeholder="All sessions"
                minWidth={200}
              />
              <SearchableSelect
                label="Class"
                value={classId}
                onChange={v => { setClassId(v); setDuesPage(0); }}
                options={classes.map(c => ({ value: c._id, label: c.name }))}
                placeholder="All classes"
                minWidth={200}
              />
              <TextField
                size="small"
                placeholder="Search by name, GR or application no.…"
                value={studentSearch}
                onChange={e => { setStudentSearch(e.target.value); setDuesPage(0); }}
                sx={{ flex: 1, minWidth: 220 }}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={overdueOnly} onChange={(_, v) => { setOverdueOnly(v); setDuesPage(0); }} />}
                label={<Typography variant="body2">Overdue only</Typography>}
              />
            </Stack>
          </Paper>

          {/* Dues grid */}
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
          }}>
            {loadingDues && !duesLoadedOnce ? skeletonDues : duesRows.length === 0 ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 34, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Nothing to collect
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {debouncedSearch
                      ? 'No students with dues match your search.'
                      : 'Every student in this scope has settled their challans — or no challans have been generated yet.'}
                  </Typography>
                </Paper>
              </Box>
            ) : (
              duesRows.map(row => {
                const sectionColor = row.section?.name
                  ? getHashedColor(String(row.section._id || row.section.name), theme.palette.mode === 'dark')
                  : null;
                const openPay = () => canWriteFees && setPayTarget({ personId: row.personId, studentId: row.studentId });
                return (
                  <Box key={row.personId || row.studentId}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2, borderRadius: 2, cursor: canWriteFees ? 'pointer' : 'default',
                        transition: 'border-color 120ms, box-shadow 120ms',
                        '&:hover': canWriteFees ? {
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                          boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                        } : {},
                      }}
                      onClick={openPay}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="baseline">
                            <Typography variant="body1" fontWeight={700} noWrap>{row.name}</Typography>
                            {payerNumber(row) && (
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                                {payerNumber(row)}
                              </Typography>
                            )}
                          </Stack>
                          {row.fatherName && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              s/o {row.fatherName}
                            </Typography>
                          )}
                        </Box>
                        {row.isOverdue && (
                          <Chip
                            size="small"
                            icon={<WarningAmber sx={{ fontSize: '0.85rem !important' }} />}
                            label="Overdue"
                            variant="outlined"
                            sx={{
                              height: 22, fontSize: '0.7rem',
                              borderColor: alpha(theme.palette.error.main, 0.3),
                              color: theme.palette.error.dark,
                              bgcolor: alpha(theme.palette.error.main, 0.06),
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        )}
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">
                          {row.class?.name ?? '—'}
                        </Typography>
                        {row.section?.name && sectionColor && (
                          <Chip
                            label={row.section.name}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 18, fontSize: '0.65rem', fontWeight: 600,
                              borderColor: alpha(sectionColor.text, 0.35),
                              color: sectionColor.text,
                              bgcolor: sectionColor.bg,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                        {row.rollNumber != null && (
                          <Typography variant="caption" color="text.secondary">· Roll {row.rollNumber}</Typography>
                        )}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Box>
                          <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{
                              color: theme.palette.warning.dark,
                              fontVariantNumeric: 'tabular-nums',
                              lineHeight: 1.1,
                              mt: 0.25,
                            }}
                          >
                            {currencyFmt(row.totalOutstanding)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.invoiceCount} challan{row.invoiceCount === 1 ? '' : 's'}
                            {row.oldestDue && ` · oldest ${format(new Date(row.oldestDue), 'MMM d')}`}
                          </Typography>
                        </Box>
                        {canWriteFees && (
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<PointOfSale fontSize="small" />}
                            onClick={(e) => { e.stopPropagation(); openPay(); }}
                            sx={{ borderRadius: 2 }}
                          >
                            Collect
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  </Box>
                );
              })
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <TablePagination
              component="div"
              count={duesTotal}
              page={duesPage}
              onPageChange={(_, p) => setDuesPage(p)}
              rowsPerPage={duesRowsPerPage}
              onRowsPerPageChange={e => { setDuesRowsPerPage(parseInt(e.target.value, 10)); setDuesPage(0); }}
              rowsPerPageOptions={[12, 24, 48, 96]}
              labelRowsPerPage="Cards per page:"
            />
          </Box>
        </>
      )}

      {tab === 'recent' && (
        <>
          {/* KPIs + filter */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Payments</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                {recentKpis.count.toLocaleString()}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                px: 2, py: 1.5, borderRadius: 2, flex: 1,
                bgcolor: alpha(theme.palette.success.main, 0.06),
                borderColor: alpha(theme.palette.success.main, 0.2),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Total collected</Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mt: 0.25, color: theme.palette.success.dark, fontVariantNumeric: 'tabular-nums' }}
              >
                {currencyFmt(recentKpis.collected)}
              </Typography>
            </Paper>
          </Stack>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                size="small"
                placeholder="Search by name, GR, application no. or receipt #…"
                value={recentSearch}
                onChange={e => { setRecentSearch(e.target.value); setRecentPage(0); }}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={includeReversed} onChange={(_, v) => { setIncludeReversed(v); setRecentPage(0); }} />}
                label={<Typography variant="body2">Include reversed</Typography>}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ pr: 2 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRecent && !recentLoadedOnce ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
                    </TableRow>
                  ))
                ) : recentRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <AccountBalance sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        No payments yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Payments show up here the moment they're recorded on the Collect tab.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentRows.map(row => (
                    <TableRow key={row._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                          {row.receiptNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.allocationCount > 0
                            ? `${row.allocationCount} challan${row.allocationCount === 1 ? '' : 's'}`
                            : 'Advance'}
                          {(row.advanceAmount ?? 0) > 0 && row.allocationCount > 0 && ` · ${currencyFmt(row.advanceAmount!)} kept as advance`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PayerCell payer={row.student} rollNumber={row.rollNumber} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{PAYMENT_METHOD_LABEL[row.method]}</Typography>
                        {row.referenceNumber && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {row.referenceNumber}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{format(new Date(row.receivedAt), 'MMM d, yyyy')}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(row.receivedAt), 'p')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {currencyFmt(row.amount)}
                        </Typography>
                        {(row.fromAdvance ?? 0) > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                            + {currencyFmt(row.fromAdvance!)} from advance
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.isActive ? 'Posted' : 'Reversed'}
                          variant="outlined"
                          sx={{
                            height: 22, fontSize: '0.72rem', fontWeight: 600,
                            ...(row.isActive
                              ? {
                                  borderColor: alpha(theme.palette.success.main, 0.32),
                                  color: theme.palette.success.dark,
                                  bgcolor: alpha(theme.palette.success.main, 0.08),
                                }
                              : {
                                  borderColor: alpha(theme.palette.text.primary, 0.16),
                                  color: 'text.disabled',
                                }),
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 1 }}>
                        <IconButton size="small" onClick={e => openMenu(e, row)} aria-label="Row actions">
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={recentTotal}
              page={recentPage}
              onPageChange={(_, p) => setRecentPage(p)}
              rowsPerPage={recentRowsPerPage}
              onRowsPerPageChange={e => { setRecentRowsPerPage(parseInt(e.target.value, 10)); setRecentPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>

          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuWithHintsProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            {menuRow && (
              <MenuItem
                disabled={preparingReceipt}
                onClick={() => { const row = menuRow; closeMenu(); void downloadReceipt(row); }}
              >
                <ListItemIcon><ReceiptLong fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Download receipt"
                  secondary={menuRow.isActive ? 'PDF, ready to print' : 'Marked reversed'}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            )}
            {canWriteFees && menuRow?.isActive && (
              <MenuItem onClick={() => menuRow && openReverse(menuRow)} sx={{ color: 'error.main' }}>
                <ListItemIcon><Undo fontSize="small" color="error" /></ListItemIcon>
                <ListItemText primary="Reverse payment" secondary="The challans it paid become due again" />
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {/* Record payment dialog */}
      <RecordFeePaymentDialog
        open={Boolean(payTarget)}
        personId={payTarget?.personId ?? null}
        studentId={payTarget?.studentId ?? null}
        onClose={() => setPayTarget(null)}
        onRecorded={() => {
          // Refresh both tabs so KPIs stay honest.
          loadDues();
          loadRecent();
        }}
      />

      <ReasonDialog
        open={!!reverseTarget}
        onClose={() => setReverseTarget(null)}
        title={`Reverse receipt ${reverseTarget?.receiptNumber ?? ''}?`}
        facts={reverseTarget ? [
          { label: 'Amount', value: currencyFmt(reverseTarget.amount) },
          { label: 'Paid by', value: reverseTarget.paidBy?.name || reverseTarget.student?.name },
          { label: 'Received', value: formatDate(reverseTarget.receivedAt) },
        ] : []}
        consequence={[
          (reverseTarget?.allocationCount ?? 0) > 0 && 'The challans it paid become due again.',
          (reverseTarget?.advanceAmount ?? 0) > 0 && 'The advance it created is taken back.',
          (reverseTarget?.fromAdvance ?? 0) > 0 && 'The advance it used is restored.',
          'The reversal is recorded in the books today; the receipt stays on record, marked reversed.',
        ].filter(Boolean).join(' ')}
        confirmText="Reverse receipt"
        onConfirm={confirmReverse}
      />
    </Box>
  );
};

export default FeeCollectionPage;
